import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { autoPostAPI, justimmoExportAPI, importBlocklistAPI, willhabenBrokerAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  Database, Users, Loader2, CheckCircle2, ArrowRight, AlertTriangle, ExternalLink,
  Trash2, RotateCcw,
} from 'lucide-react';
import WillhabenSubscriptionPanel from '../components/WillhabenSubscriptionPanel';

/**
 * The single canonical place where the user manages WHERE their
 * properties come from. Replaces the previous scattered setup:
 *   * Justimmo lived on /justimmo with its own page
 *   * Willhaben subscription was buried inside AutoPostSettings
 *
 * This hub gives both equal billing, surfaces the duplicate-import
 * warning when the user has both connected, and is the page we
 * surface during post-upgrade onboarding.
 *
 * Design intent: each source gets a card. If connected, the card
 * shows a green status + brief stats + a Manage button. If not,
 * a clear "Verknüpfen" CTA. The cards stay narrow + scannable
 * regardless of how many sources we add later (Re/Max API, IS24,
 * etc.). The layout scales by adding more cards, not by getting
 * deeper.
 */
export default function ImportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [justimmo, setJustimmo] = useState(null);   // {connected, ownerName, propertyCount}
  const [autoConfig, setAutoConfig] = useState(null); // raw auto_post_config (for willhaben subscription state)
  const [loading, setLoading] = useState(true);
  // Capture the actual error per-source so we can distinguish "this
  // user has no source connected" (true negative) from "the API call
  // failed" (transient error worth retrying). Without this distinction
  // the user sees "no source connected" even when the backend was
  // briefly unavailable, then enables auto-post against an empty
  // pipeline.
  const [loadError, setLoadError] = useState(null);

  // Deleted (import-blocked) listings — tombstones written when the user
  // deletes an imported property. Shown with a "Wieder importieren"
  // action that removes the block.
  const [blocked, setBlocked] = useState([]);
  const [unblockingId, setUnblockingId] = useState(null);

  const load = React.useCallback(() => {
    setLoadError(null);
    setLoading(true);
    Promise.all([
      // 404 from /justimmo-export/status is NOT an error. That endpoint
      // does NOT 404, it returns {connected:false}. Any other 4xx/5xx
      // is a real error worth surfacing to the user.
      justimmoExportAPI.getStatus(),
      autoPostAPI.getConfig(),
      // Blocklist is non-critical — an error just hides the section.
      importBlocklistAPI.list().catch(() => ({ data: [] })),
    ]).then(([js, ap, bl]) => {
      setJustimmo(js.data || { connected: false });
      setAutoConfig(ap.data || {});
      setBlocked(Array.isArray(bl.data) ? bl.data : []);
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      setLoadError(err);
    });
  }, []);

  const handleUnblock = async (entry) => {
    setUnblockingId(entry.id);
    try {
      await importBlocklistAPI.unblock(entry.id);
      setBlocked((cur) => cur.filter((b) => b.id !== entry.id));
      // Willhaben + active subscription: trigger a rescan right away so
      // the listing comes back within a minute instead of at the next
      // nightly sync. Best-effort — the nightly scan catches it anyway.
      if (entry.source === 'willhaben' && autoConfig?.willhaben_subscription_org_id) {
        willhabenBrokerAPI.rescan().catch(() => {});
        toast.success('Freigegeben. Der Abgleich wurde gestartet — das Inserat wird gleich wieder importiert.');
      } else if (entry.source === 'justimmo') {
        toast.success('Freigegeben. Importiere das Inserat über deine Justimmo-Übersicht erneut.');
      } else {
        toast.success('Freigegeben. Das Inserat kann wieder importiert werden.');
      }
    } catch (e) {
      toast.error('Freigeben fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setUnblockingId(null);
    }
  };

  useEffect(() => { load(); }, [load]);

  const justimmoConnected = !!justimmo?.connected;
  const willhabenConnected = !!autoConfig?.willhaben_subscription_org_id;
  const bothConnected = justimmoConnected && willhabenConnected;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#23211a] mb-1">
            {t('imports.title') || 'Datenquellen'}
          </h1>
          <p className="text-[#6f6a5c] text-sm leading-relaxed">
            {t('imports.subtitle')
              || 'Verbinde, von wo deine Immobilien kommen sollen. Listings werden automatisch importiert und stehen sofort für Videos und Auto-Post zur Verfügung.'}
          </p>
        </div>

        {/* Duplicate-import warning when BOTH sources are active */}
        {bothConnected && (
          <Card className="glass border-[#c7a869] bg-[#c7a869]/5">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#9a6d1f] shrink-0 mt-0.5" />
              <div className="text-xs text-[#7a5619] leading-relaxed">
                <strong className="text-[#9a6d1f]">
                  {t('imports.dupWarningTitle') || 'Achtung: Beide Quellen sind aktiv'}
                </strong>
                <p className="mt-1 text-[#7a5619]/90">
                  {t('imports.dupWarningBody')
                    || 'Wenn dieselbe Immobilie auf Justimmo und Willhaben gelistet ist, importieren wir sie zweimal. Empfehlung: nutze nur eine Quelle als deinen primären Kanal. Meistens dein CRM (Justimmo). Und deaktiviere die andere.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Load-error banner: distinguishes a real API failure from
            "user has no sources connected". Without it, a transient
            backend error renders as "no source connected". The user
            then enables auto-post against an empty pipeline. */}
        {loadError && !loading && (
          <Card className="glass border-[#b3543f]/50 bg-[#b3543f]/5">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#a34430] shrink-0 mt-0.5" />
              <div className="flex-1 text-xs text-[#8c3a29] leading-relaxed">
                <strong className="text-[#8c3a29]">
                  {t('imports.loadFailedTitle') || 'Status konnte nicht geladen werden'}
                </strong>
                <p className="mt-1 text-[#8c3a29]/90">
                  {t('imports.loadFailedBody')
                    || 'Wir konnten den Verbindungsstatus deiner Datenquellen nicht abrufen. Bitte erneut versuchen. Die Daten unten könnten unvollständig sein.'}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={load} className="text-[#8c3a29] hover:bg-[#f3ddd5]">
                {t('imports.retry') || 'Erneut'}
              </Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card className="glass border-[#e3dccb]">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#1f4d3a] mx-auto" />
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ── Justimmo card ─────────────────────────────────── */}
            <Card className={`glass ${justimmoConnected ? 'border-[#1e7a4f]/40' : 'border-[#e3dccb]'}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${justimmoConnected ? 'bg-[#e4f0e8] border border-[#1e7a4f]/40' : 'bg-[#e9e2d2]'} shrink-0`}>
                      <Database className={`w-5 h-5 ${justimmoConnected ? 'text-[#1e7a4f]' : 'text-[#6f6a5c]'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-[#23211a] flex items-center gap-2">
                        Justimmo
                        {justimmoConnected && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1e7a4f]/90 text-white">
                            {t('imports.connected') || 'Verbunden'}
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription>
                        {t('imports.justimmoDesc')
                          || 'CRM-Sync. Alle deine Justimmo-Listings stehen automatisch zur Verfügung.'}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {justimmoConnected && (
                  <div className="text-xs text-[#4a463b] bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md p-3 space-y-1">
                    {justimmo?.api_username && (
                      <p>
                        <span className="text-[#8a8474]">{t('imports.account') || 'Konto'}:</span>{' '}
                        <strong className="text-[#1e7a4f]">{justimmo.api_username}</strong>
                      </p>
                    )}
                    {justimmo?.default_owner_id && (
                      <p>
                        <span className="text-[#8a8474]">{t('imports.scopedTo') || 'Inserate von'}:</span>{' '}
                        <strong className="text-[#1e7a4f]">Mitarbeiter #{justimmo.default_owner_id}</strong>
                      </p>
                    )}
                    {typeof justimmo?.total_properties === 'number' && (
                      <p>
                        <span className="text-[#8a8474]">{t('imports.imported') || 'Importiert'}:</span>{' '}
                        {justimmo.total_properties} {t('imports.properties') || 'Immobilien'}
                      </p>
                    )}
                  </div>
                )}
                <Button
                  onClick={() => navigate('/justimmo')}
                  className={justimmoConnected
                    ? 'bg-[#e9e2d2] hover:bg-[#e3dccb] text-[#23211a]'
                    : 'bg-[#1f4d3a] hover:bg-[#163a2c] text-white'}
                  data-testid="imports-manage-justimmo"
                >
                  {justimmoConnected
                    ? (t('imports.manage') || 'Verwalten')
                    : (t('imports.connectJustimmo') || 'Justimmo verbinden')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* ── Willhaben card ────────────────────────────────── */}
            <Card className={`glass ${willhabenConnected ? 'border-[#1e7a4f]/40' : 'border-[#e3dccb]'}`}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${willhabenConnected ? 'bg-[#e4f0e8] border border-[#1e7a4f]/40' : 'bg-[#e9e2d2]'} shrink-0`}>
                    <Users className={`w-5 h-5 ${willhabenConnected ? 'text-[#1e7a4f]' : 'text-[#6f6a5c]'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-[#23211a] flex items-center gap-2">
                      Willhaben
                      {willhabenConnected && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1e7a4f]/90 text-white">
                          {t('imports.connected') || 'Verbunden'}
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {t('imports.willhabenDesc')
                        || 'Verlinke dein Anbieter-Profil. Wir scannen täglich nach neuen Inseraten unter deinem Namen und importieren sie automatisch.'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <WillhabenSubscriptionPanel t={t} />
                <p className="text-[11px] text-[#8a8474] mt-3 leading-relaxed">
                  {t('imports.willhabenAlsoBulk')
                    || 'Tipp: Über „Neue Immobilie" → „Anbieter-Profil" kannst du auch jederzeit alle Inserate eines Willhaben-Anbieters auf einmal importieren. Auch ohne aktives Abo dieser Quelle.'}
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {/* Deleted (import-blocked) listings. Only rendered when there
            are tombstones — most users never see this. */}
        {blocked.length > 0 && (
          <Card className="glass border-[#e3dccb]">
            <CardHeader>
              <CardTitle className="text-[#23211a] flex items-center gap-2 text-base">
                <Trash2 className="w-4 h-4 text-[#6f6a5c]" />
                {t('imports.blockedTitle') || 'Gelöschte Inserate'}
              </CardTitle>
              <CardDescription>
                {t('imports.blockedDesc')
                  || 'Diese Inserate hast du gelöscht — sie werden nicht mehr automatisch importiert. Mit „Wieder importieren" gibst du sie wieder frei.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {blocked.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#fffdf7] border border-[#e3dccb]"
                  data-testid={`blocked-import-${b.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-[#23211a] truncate">{b.title || b.external_id}</p>
                    <p className="text-[11px] text-[#8a8474] truncate">
                      <span className={`inline-block px-1.5 py-0.5 rounded mr-1.5 text-[10px] font-medium uppercase tracking-wide ${
                        b.source === 'justimmo' ? 'bg-[#dbe7f1] text-[#2b5b8a]' : 'bg-[#e4f0e8] text-[#1e7a4f]'
                      }`}>
                        {b.source}
                      </span>
                      {b.location ? `${b.location} · ` : ''}
                      gelöscht {b.deleted_at ? new Date(b.deleted_at).toLocaleDateString('de-AT') : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={unblockingId === b.id}
                    onClick={() => handleUnblock(b)}
                    className="border-[#c9c1ac] text-[#23211a] hover:bg-[#efe9db] shrink-0"
                    data-testid={`unblock-import-${b.id}`}
                  >
                    {unblockingId === b.id
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                    {t('imports.reimport') || 'Wieder importieren'}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Help footer */}
        <Card className="glass border-[#e3dccb] bg-[#f2ecdf]">
          <CardContent className="p-4 text-xs text-[#6f6a5c] leading-relaxed">
            <p>
              <strong className="text-[#4a463b]">{t('imports.helpHeader') || 'Wie hängt das alles zusammen?'}</strong>
            </p>
            <ul className="mt-2 space-y-1 list-disc pl-5">
              <li>{t('imports.helpRow1') || 'Importierte Listings landen in deinen "Immobilien" und sind sofort für Videos verfügbar.'}</li>
              <li>{t('imports.helpRow2') || 'Hast du Auto-Post aktiviert, werden neue Listings auch dort automatisch berücksichtigt.'}</li>
              <li>{t('imports.helpRow3') || 'Du kannst jederzeit eine Quelle trennen. Bereits importierte Immobilien bleiben erhalten.'}</li>
            </ul>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/properties')}
              className="text-[#4a463b] hover:bg-[#efe9db] mt-3 px-2"
            >
              {t('imports.toProperties') || 'Zu meinen Immobilien'}
              <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
